import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  MarkerType,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrNodes } from '@/api/ivrNodes';
import { ivrEdges } from '@/api/ivrEdges';
import type { IvrNodeRow } from '@/api/ivrNodes';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import type { IvrTreeRow } from '@/api/ivrTrees';

import { NodeEditorDrawer } from '@/components/ivr/NodeEditorDrawer';
import { IvrCanvasNode } from './IvrCanvasNode';
import { IvrCanvasEdge } from './IvrCanvasEdge';
import { EdgeInlineEditor } from './EdgeInlineEditor';
import { CreateNodePopover } from './CreateNodePopover';
import { ChangeParentModal } from './ChangeParentModal';
import { TemplateEmptyState } from './TemplateEmptyState';

import dagre from '@dagrejs/dagre';

// ── Dagre auto-layout ──────────────────────────────────────────────────────────

const NODE_W = 240;
const NODE_H = 90;

function computeDagreLayout(
  nodeRows: IvrNodeRow[],
  edgeRows: IvrEdgeRow[],
): Map<string, { x: number; y: number }> {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 50 });

  for (const n of nodeRows) {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  }
  for (const e of edgeRows) {
    if (e.from_node_id && e.to_node_id) {
      g.setEdge(e.from_node_id, e.to_node_id);
    }
  }

  dagre.layout(g);

  const positions = new Map<string, { x: number; y: number }>();
  for (const n of nodeRows) {
    const node = g.node(n.id);
    if (node) {
      positions.set(n.id, {
        x: node.x - NODE_W / 2,
        y: node.y - NODE_H / 2,
      });
    }
  }
  return positions;
}

// ── Type registries ────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodeTypes: Record<string, any> = { ivrNode: IvrCanvasNode };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const edgeTypes: Record<string, any> = { ivrEdge: IvrCanvasEdge };

// ── Inner canvas ───────────────────────────────────────────────────────────────

interface InnerCanvasProps {
  tree: IvrTreeRow;
  canMutate: boolean;
  treeId: string;
}

function InnerCanvas({ tree, canMutate, treeId }: InnerCanvasProps) {
  const qc = useQueryClient();
  const { fitView, getViewport } = useReactFlow();

  // ── Data queries ─────────────────────────────────────────────────────────

  const { data: nodesData, isLoading: nodesLoading } = useQuery({
    queryKey: ['ivrNodes', treeId],
    queryFn: () => ivrNodes.listForTree(treeId),
    staleTime: 5000,
  });

  const { data: edgesData, isLoading: edgesLoading } = useQuery({
    queryKey: ['ivrEdgesForTree', treeId],
    queryFn: () => ivrEdges.listForTree(treeId),
    staleTime: 5000,
  });

  const treeNodes: IvrNodeRow[] = nodesData?.rows ?? [];
  const treeEdges: IvrEdgeRow[] = edgesData?.rows ?? [];

  // ── UI state ──────────────────────────────────────────────────────────────

  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null);
  const [editNodeId, setEditNodeId] = useState<string | undefined>(undefined);

  const [edgeEditor, setEdgeEditor] = useState<{
    edgeId: string;
    fromNodeId: string;
    toNodeId: string | null;
    branchKey: string;
    screenX: number;
    screenY: number;
  } | null>(null);

  const [createPopover, setCreatePopover] = useState<{
    fromNodeId: string | null;
    screenX: number;
    screenY: number;
    spawnX: number;
    spawnY: number;
  } | null>(null);

  const [changeParent, setChangeParent] = useState<{
    nodeId: string;
    nodeName: string;
    currentParentId: string | null;
  } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // ── React Flow state ──────────────────────────────────────────────────────

  // useNodesState returns [nodes, setNodes, onNodesChange]. The third
  // element MUST be passed to ReactFlow as `onNodesChange` — without it
  // React Flow has no way to update node positions during drag, so the
  // node visually stays in place even though the drag-stop handler
  // saves the new position to the backend. (Bug Sam reported
  // 2026-05-21: "i move an ivr it doesnt look like it moved i have to
  // refresh to see that it moved".)
  const [rfNodes, setRfNodes, onRfNodesChange] = useNodesState<Node>([]);
  const [rfEdges, setRfEdges, onRfEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // ── Stable callbacks (pass into node data) ────────────────────────────────

  const handleEdit = useCallback((id: string) => {
    setEditNodeId(id);
    setDrawerMode('edit');
  }, []);

  const handleChangeParent = useCallback((id: string) => {
    const node = treeNodes.find((n) => n.id === id);
    const incomingEdge = treeEdges.find((e) => e.to_node_id === id);
    setChangeParent({
      nodeId: id,
      nodeName: node?.name ?? '',
      currentParentId: incomingEdge?.from_node_id ?? null,
    });
  }, [treeNodes, treeEdges]);

  const handleAddChildEdge = useCallback((fromId: string) => {
    const rfNode = rfNodes.find((n: Node) => n.id === fromId);
    const spawnX = rfNode ? rfNode.position.x : 0;
    const spawnY = rfNode ? rfNode.position.y + 180 : 200;
    const vp = getViewport();
    const screenX = spawnX * vp.zoom + vp.x + 100;
    const screenY = spawnY * vp.zoom + vp.y - 50;
    setCreatePopover({
      fromNodeId: fromId,
      screenX: Math.max(8, screenX),
      screenY: Math.max(8, screenY),
      spawnX,
      spawnY,
    });
  }, [rfNodes, getViewport]);

  const handleDelete = useCallback((id: string) => {
    setDeleteConfirm(id);
  }, []);

  // ── Position save on drag ─────────────────────────────────────────────────

  const dragSaveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const handleNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    if (!canMutate) return;
    const existing = dragSaveTimers.current.get(node.id);
    if (existing) clearTimeout(existing);
    dragSaveTimers.current.set(
      node.id,
      setTimeout(() => {
        ivrNodes.update(node.id, {
          position_x: node.position.x,
          position_y: node.position.y,
        }).catch(() => { /* silent */ });
        dragSaveTimers.current.delete(node.id);
      }, 300),
    );
  }, [canMutate]);

  // ── Delete mutation ───────────────────────────────────────────────────────

  const deleteNode = useMutation({
    mutationFn: (id: string) => ivrNodes.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrTree', treeId] });
      setDeleteConfirm(null);
    },
  });

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeId) {
        e.preventDefault();
        handleDelete(selectedNodeId);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedEdgeId) {
        e.preventDefault();
        ivrEdges.remove(selectedEdgeId).then(() => {
          qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
        });
        setSelectedEdgeId(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedNodeId, selectedEdgeId, handleDelete, qc, treeId]);

  // ── Sync data → React Flow ────────────────────────────────────────────────

  const hasAutoLayouted = useRef(false);

  const batchPatchPositions = useMutation({
    mutationFn: async (positions: Array<{ id: string; x: number; y: number }>) => {
      await Promise.all(
        positions.map((p) =>
          ivrNodes.update(p.id, { position_x: p.x, position_y: p.y }),
        ),
      );
    },
  });

  useEffect(() => {
    if (!treeNodes.length) return;

    const needsLayout = treeNodes.some((n) => n.position_x === null || n.position_y === null);
    let positions: Map<string, { x: number; y: number }>;

    if (needsLayout && !hasAutoLayouted.current) {
      hasAutoLayouted.current = true;
      positions = computeDagreLayout(treeNodes, treeEdges);
      const toBatch = treeNodes.map((n) => {
        const p = positions.get(n.id)!;
        return { id: n.id, x: p.x, y: p.y };
      });
      batchPatchPositions.mutate(toBatch);
    } else {
      positions = new Map(
        treeNodes.map((n, idx) => [
          n.id,
          n.position_x !== null && n.position_y !== null
            ? { x: parseFloat(n.position_x!), y: parseFloat(n.position_y!) }
            : { x: idx * 260, y: 0 },
        ]),
      );
    }

    const newRfNodes: Node[] = treeNodes.map((n) => {
      const pos = positions.get(n.id) ?? { x: 0, y: 0 };
      return {
        id: n.id,
        type: 'ivrNode',
        position: pos,
        data: {
          id: n.id,
          ref_number: n.ref_number,
          name: n.name,
          node_type: n.node_type,
          active: n.active,
          isRoot: n.id === tree.root_node_id,
          onEdit: handleEdit,
          onChangeParent: handleChangeParent,
          onAddChild: handleAddChildEdge,
          onDelete: handleDelete,
          onAddChildEdge: handleAddChildEdge,
        },
        selected: n.id === selectedNodeId,
      };
    });

    const newRfEdges: Edge[] = treeEdges
      .filter((e) => e.from_node_id && e.to_node_id)
      .map((e) => ({
        id: e.id,
        source: e.from_node_id,
        target: e.to_node_id!,
        type: 'ivrEdge',
        data: {
          branchKey: e.branch_key,
          edgeId: e.id,
          onEdgeClick: (edgeId: string) => {
            const edgeRow = treeEdges.find((r) => r.id === edgeId);
            if (!edgeRow || !canMutate) return;
            setEdgeEditor({
              edgeId,
              fromNodeId: edgeRow.from_node_id,
              toNodeId: edgeRow.to_node_id,
              branchKey: edgeRow.branch_key,
              screenX: window.innerWidth / 2,
              screenY: window.innerHeight / 2,
            });
          },
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 16,
          height: 16,
          color: '#cbd5e1',
        },
        selected: e.id === selectedEdgeId,
      }));

    setRfNodes(newRfNodes);
    setRfEdges(newRfEdges);

    if (needsLayout && hasAutoLayouted.current) {
      setTimeout(() => fitView({ padding: 0.15, minZoom: 0.8, maxZoom: 1.2, duration: 400 }), 100);
    }
    // selectedNodeId / selectedEdgeId are INTENTIONALLY excluded from
    // deps — they're handled by the lightweight selection-sync effects
    // below. Including them here caused every click to rebuild rfNodes
    // from server data, which would clobber any local drag-positions
    // that hadn't round-tripped through the backend yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeNodes, treeEdges, tree.root_node_id]);

  // Selection sync — flip the `selected` flag on existing rfNodes /
  // rfEdges without rebuilding them. Cheap, won't disturb any
  // mid-flight drag state managed internally by React Flow.
  useEffect(() => {
    setRfNodes((prev) => prev.map((n) =>
      n.selected === (n.id === selectedNodeId) ? n : { ...n, selected: n.id === selectedNodeId },
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  useEffect(() => {
    setRfEdges((prev) => prev.map((e) =>
      e.selected === (e.id === selectedEdgeId) ? e : { ...e, selected: e.id === selectedEdgeId },
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEdgeId]);

  // ── Loading ───────────────────────────────────────────────────────────────

  if (nodesLoading || edgesLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-slate-400">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-400" />
          Loading canvas…
        </div>
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  if (treeNodes.length === 0) {
    return (
      <TemplateEmptyState
        treeId={treeId}
        onApplied={() => {
          hasAutoLayouted.current = false;
          qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
          qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
        }}
        onAddNode={() => {
          setEditNodeId(undefined);
          setDrawerMode('create');
        }}
      />
    );
  }

  // ── Canvas ────────────────────────────────────────────────────────────────

  return (
    <div className="relative h-full w-full">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onRfNodesChange}
        onEdgesChange={onRfEdgesChange}
        onNodeDragStop={handleNodeDragStop}
        onEdgeClick={(_e: React.MouseEvent, edge: Edge) => {
          const edgeRow = treeEdges.find((r) => r.id === edge.id);
          if (!edgeRow || !canMutate) return;
          setEdgeEditor({
            edgeId: edge.id,
            fromNodeId: edgeRow.from_node_id,
            toNodeId: edgeRow.to_node_id,
            branchKey: edgeRow.branch_key,
            screenX: window.innerWidth / 2,
            screenY: window.innerHeight / 2,
          });
        }}
        onNodeClick={(_e: React.MouseEvent, node: Node) => {
          setSelectedNodeId(node.id);
          setSelectedEdgeId(null);
        }}
        onPaneClick={() => {
          setSelectedNodeId(null);
          setSelectedEdgeId(null);
        }}
        fitView
        // Cap the auto-shrink at 0.8 so a small tree (10ish nodes spread
        // wide) doesn't become unreadable on first paint. Users who want
        // a 30,000-foot view can still pinch / Ctrl+scroll down to
        // minZoom (0.1). Sam 2026-05-21.
        fitViewOptions={{ padding: 0.15, minZoom: 0.8, maxZoom: 1.2 }}
        minZoom={0.1}
        maxZoom={2.5}
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#e2e8f0"
        />
        <Controls className="!border-slate-200 !bg-white !shadow-sm" showInteractive />
        <MiniMap
          nodeColor={(node: Node) => {
            if (!node.data?.active) return '#f1f5f9';
            if (node.data?.isRoot) return '#fbbf24';
            return '#c7d2fe';
          }}
          maskColor="rgba(248,250,252,0.7)"
          className="!border-slate-200 !bg-white !shadow-sm"
        />
      </ReactFlow>

      {/* Drawer */}
      {drawerMode && (
        <NodeEditorDrawer
          mode={drawerMode}
          treeId={treeId}
          nodeId={editNodeId}
          treeNodes={treeNodes}
          canMutate={canMutate}
          onClose={() => { setDrawerMode(null); setEditNodeId(undefined); }}
          onSaved={() => {
            setDrawerMode(null);
            setEditNodeId(undefined);
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
          }}
          onDeleted={() => {
            setDrawerMode(null);
            setEditNodeId(undefined);
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
            qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
          }}
        />
      )}

      {/* Edge inline editor */}
      {edgeEditor && (
        <EdgeInlineEditor
          edgeId={edgeEditor.edgeId}
          fromNodeId={edgeEditor.fromNodeId}
          toNodeId={edgeEditor.toNodeId}
          branchKey={edgeEditor.branchKey}
          treeId={treeId}
          treeNodes={treeNodes}
          canMutate={canMutate}
          x={edgeEditor.screenX}
          y={edgeEditor.screenY}
          onClose={() => setEdgeEditor(null)}
        />
      )}

      {/* Create node popover */}
      {createPopover && (
        <CreateNodePopover
          treeId={treeId}
          fromNodeId={createPopover.fromNodeId}
          spawnX={createPopover.spawnX}
          spawnY={createPopover.spawnY}
          screenX={createPopover.screenX}
          screenY={createPopover.screenY}
          onCreated={(newId) => {
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
            qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
            setEditNodeId(newId);
            setDrawerMode('edit');
          }}
          onClose={() => setCreatePopover(null)}
        />
      )}

      {/* Change parent modal */}
      {changeParent && (
        <ChangeParentModal
          nodeId={changeParent.nodeId}
          nodeName={changeParent.nodeName}
          treeId={treeId}
          treeNodes={treeNodes}
          currentParentId={changeParent.currentParentId}
          onClose={() => setChangeParent(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
          }}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/20"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="fixed left-1/2 top-1/2 z-50 w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <p className="text-sm font-semibold text-slate-800">Delete node?</p>
            <p className="mt-1 text-xs text-slate-500">
              This will remove the node and all its edges permanently.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteNode.mutate(deleteConfirm!)}
                disabled={deleteNode.isPending}
                className="flex-1 rounded-md bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteNode.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add node button */}
      {canMutate && (
        <div className="absolute left-4 top-4 z-10">
          <button
            type="button"
            onClick={() => {
              setEditNodeId(undefined);
              setDrawerMode('create');
            }}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-800 transition-colors"
          >
            <span className="text-lg leading-none">+</span>
            Add node
          </button>
        </div>
      )}
    </div>
  );
}

// ── Public export ──────────────────────────────────────────────────────────────

interface IvrTreeCanvasProps {
  tree: IvrTreeRow;
  canMutate: boolean;
}

export function IvrTreeCanvas({ tree, canMutate }: IvrTreeCanvasProps) {
  return (
    <div style={{ height: 'calc(100vh - 220px)', minHeight: '400px' }} className="w-full">
      <ReactFlowProvider>
        <InnerCanvas tree={tree} canMutate={canMutate} treeId={tree.id} />
      </ReactFlowProvider>
    </div>
  );
}
